import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cancelSession } from "../services/cancel-session";

export function useCancelSession() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      source_id,
      reason,
    }: {
      source_id: string;
      reason?: string;
    }) => cancelSession(source_id, reason),
    // The cancel endpoint now marks the session cancelled before it responds,
    // so one round of invalidation shows the truth. This used to refetch at
    // 0s / 1s / 2.5s / 4s and then reload the whole page, because the status
    // was written asynchronously by Calendly's invitee.canceled webhook and an
    // early refetch could still come back ACTIVE. Chasing that with a reload
    // cost the operator their scroll position, filters, page number and open
    // drawer on every cancellation — and, with a 30-minute staleTime on the
    // sessions list, an early refetch could re-stamp the pre-cancel row as
    // fresh for another half hour.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["sessions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      queryClient.refetchQueries({ queryKey: ["user"], exact: false });
      toast({
        title: "Success",
        description: "Session Cancelled Successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to Cancel Session",
        variant: "destructive",
      });
    },
  });
}
